<template>
  <section class="mb-8">
    <p class="mb-4">This Privacy Policy describes how Claims For Cars (a trading style of JLG
      Legal
      Limited, referred as "Johnson Law Group", "JLG UK," "we," "our," or "us"), collects, uses,
      and shares information about you in the course of providing legal, advisory and/or
      consultancy services and carrying out marketing activities. It also describes your privacy
      rights under the data protection law. It also applies to your use of our websites (<a
        href="https://johnsonlawgroup.co.uk/" class="text-green-600 hover:underline">https://johnsonlawgroup.co.uk/</a>)
      and all features, content, and other services that we own, control, and make available to
      you (collectively, the "Website(s)").</p>

    <p class="mb-4">It is important that you read this privacy policy together with any other
      privacy policy or fair processing notice we may provide on specific occasions when we are
      collecting or processing personal data about you so that you are fully aware of how and why
      we are using your data. This privacy policy supplements other notices and privacy policies
      and is not intended to override them.</p>
  </section>

  <section class="mb-8">
    <h2 class="text-xl font-bold mb-4">Controller</h2>
    <p class="mb-4">JLG UK is the controller and responsible for your personal data.</p>
    <p class="mb-4">If you have any questions about this Privacy Policy or wish to exercise <b>"Your
      Rights"</b>, please contact us using the details in the <b>"Contact Us"</b> section below.
    </p>

    <h3 class="mb-2 italic">Third Party Links</h3>
    <p class="mb-4">We may offer portions of our services through a third party or include or
      offer third party products or services, or links to third party websites, locations,
      platforms, and services operated and owned by third parties on our Website, subject to your
      preferences (where applicable). This also includes where you connect to our Website through
      a third party site. For example, on social networks such as Facebook and Twitter. We do not
      control these third party websites and are not responsible for their privacy policies. When
      you leave our Website, we encourage you to read the privacy policy of every website you
      visit.</p>
  </section>

  <section class="mb-8">
    <h2 class="text-xl font-bold mb-4 underline italic">Who Does This Privacy Policy Apply
      To?</h2>
    <p class="mb-4 underline">This Privacy Policy applies to individuals in the following
      categories below or who work for any of the following:</p>
    <ul class="list-disc pl-8 mb-4 space-y-2">
      <li>our clients;</li>
      <li>other lawyers and law firms, including barristers;</li>
      <li>people who are involved in court or other legal proceedings (including legal claims,
        criminal actions, inquests, tribunals, arbitrations, mediations, investigations and
        regulatory actions) or the provision of related or other legal, advisory and/or
        consultancy services, or people who are involved in claims we are handling for clients.
        This may include claimants, defendants, witnesses, experts and service providers related
        to such court or other legal proceedings, services or claims, such as investigators,
        mediators and costs lawyers;
      </li>
      <li>people who are involved in contracts and transactions we are working on, such as other
        businesses or individuals our clients are contracting with;
      </li>
      <li>our regulators, insurers, auditors, professional advisers and
        certification/accreditation bodies;
      </li>
      <li>people who use our Website; and</li>
      <li>people whose details we process in connection with our marketing activities.</li>
    </ul>
  </section>

  <section class="mb-8">
    <h2 class="text-xl font-bold mb-4 underline">What Personal Data Do We Collect?</h2>
    <p class="mb-4">Personal data, or personal information, means any information about an
      individual from which that person can be identified. It does not include data where the
      individual cannot be identified (anonymous data).</p>
    <p class="mb-4">In the course of our business, we will need to collect and process various
      types of personal data for various purposes. We will however only process and collect
      personal data where we have a lawful basis to do so. Below we have listed the types of
      personal data we normally expect to collect and process about you:</p>

    <ul class="list-disc pl-8 mb-4 space-y-2">
      <li><strong>identity and contact data</strong> for individuals (such as full name, job
        title, organisation, date of birth, address, email address and telephone number). We may
        collect additional data to enable the identity of individuals to be verified;
      </li>
      <li><strong>financial and payment data</strong>, including your bank account and payment
        card details, other related billing information and other data necessary for processing
        payments and fraud prevention;
      </li>
      <li><strong>information relevant to our legal advice and your case,</strong> including
        personal data relevant to any dispute, grievance, investigation, arbitration or other
        legal advice we have been asked to provide to you as our client, your requirements and
        personal or professional situation;
      </li>
      <li><strong>enquiries data,</strong> including the content of messages and correspondence
        you send to us (including via social media), such as questions and information you
        provide;
      </li>
      <li><strong>visitor data</strong>, including details of your visits to our premises;</li>
      <li>information about individuals employed by or associated with our clients, advisers or
        the organisations involved in a matter on which we are instructed;
      </li>
      <li><strong>criminal convictions and offences data</strong> may be processed if necessary,
        for the legal matter we are advising on;
      </li>
      <li><strong>special category personal data</strong> may be processed if necessary for the
        legal matter we are advising on, this includes information revealing racial or ethnic
        origin, political opinions, religious or philosophical beliefs, or trade union membership,
        genetic data, biometric data processed for the purpose of uniquely identifying a natural
        person, data concerning health or data concerning a natural person's sex life or sexual
        orientation;
      </li>
      <li><strong>marketing and communications data</strong>, including your marketing preferences
        and interests and any feedback you provide to us (for example, by completing a survey);
        and
      </li>
      <li><strong>technical data</strong>, including information about your browsing activities
        and your equipment collected automatically through our Website, such as:
        <ul class="list-disc pl-8 mt-2 space-y-1">
          <li>the webpages you visit;</li>
          <li>the emails and advertisements you view;</li>
          <li>the time of day you browse;</li>
          <li>referring and exiting pages;</li>
          <li>information about your device such as your device type, browser, operating system,
            internet service provider, regional and language settings;
          </li>
          <li>device identifiers such as IP address; and</li>
          <li>location data, including imprecise location data (such as location derived from an
            IP address or data that indicates a city or postal code level).
          </li>
        </ul>
        <p class="mt-2">We partner with All Response Media Ltd. to analyse and optimise our media campaigns. To help deliver our marketing strategies more effectively and ensure the ads you receive from us are relevant, they process data on your interactions with our website. This includes the use of analysis cookies through their Tag4ARM tracking system, which captures data about your web activity. Please note that this data is processed in aggregate and does not personally identify you as an individual. The purpose of this processing is solely to improve the performance and relevance of our advertising campaigns.</p>
      </li>
    </ul>

    <p class="mb-4">For further details about technical data we collect automatically from your
      use of our Website, please see the <a
        class="italic font-bold text-green-600 hover:underline"
        href="https://claims.johnsonlawgroup.co.uk/privacy-policy.php#Cookies">"Cookies"</a>
      section below.</p>

    <h3 class="mb-2 italic">If you fail to provide personal data</h3>
    <p class="mb-4">Where we need to collect personal data by law, or under the terms of a
      contract we have with you, and you do not provide that data when requested, we may not be
      able to perform the contract we have or are trying to enter into with you (for example to
      provide you with our services). Should this happen and we are unable to provide you with any
      services you have requested, we will notify you.</p>
  </section>

  <section class="mb-8">
    <h2 class="text-xl font-bold mb-4 underline">How Do We Collect Your Personal Data?</h2>

    <h3 class="text-xl font-semibold mb-2 italic">Information You Provide through direct
      interactions with us</h3>
    <p class="mb-4">We collect information you provide directly to us, such as when you:</p>
    <ul class="list-disc pl-8 mb-4 space-y-2">
      <li>request a free case evaluation from us;</li>
      <li>email us or send us a letter;</li>
      <li>speak with us through a live chat;</li>
      <li>speak with us in person or on the phone; or</li>
      <li>give us feedback.</li>
    </ul>

    <h3 class="text-xl mb-2 italic">Information collected through automated
      tracking
      technologies or interactions</h3>
    <p class="mb-4">As you interact with our Website, we will automatically collect technical data
      (as set out above). We use various technologies to collect this data ("Tracking
      Technologies"), including the following:</p>
    <ul class="list-disc pl-8 mb-4 space-y-2">
      <li>Log Files, which are files that record events that occur in connection with your use of
        our Website.
      </li>
      <li>Cookies, which are small files transferred to your device through your Web browser that
        enable websites to recognise your browser and capture and remember certain information.
        Cookies make it easier for you to navigate our Website and help us to personalise your
        experience, support security features, and bring you advertising. Cookies generally may be
        controlled through your browser settings.
      </li>
      <li>Pixels (also known as web beacons), which is code embedded in a website, email, or
        advertisement that sends information about your use to a server. There are various types
        of pixels, including image pixels (which are small graphic images) and JavaScript pixels
        (which contains JavaScript code). When you access a website, video, email, or
        advertisement that contains a pixel, the pixel may permit us or a third party to drop or
        read cookies on your browser. Pixels are used in combination with cookies to track
        activity by a particular browser on a particular device. We may incorporate pixels from
        third parties that allow us to track our conversions (understand whether you came to us
        through a particular advert, link or social media post), bring you advertising, and
        provide you with additional functionality.
      </li>
    </ul>

    <p class="mb-4">We may also receive your technical data if you visit other websites employing
      our cookies. Please see the "Cookies" section below for further details.</p>

    <h3 class="text-xl font-semibold mb-2 italic">Information from Third Parties or Publicly
      Available Sources</h3>
    <p class="mb-4">We also collect your personal data from other sources, including:</p>
    <ul class="list-disc pl-8 mb-4 space-y-2">
      <li>Our clients, agents, and professional advisers instructed by us.</li>
      <li>Lead generators, who provide us with your information so that we may contact you to
        verify your information and evaluate your potential claims.
      </li>
      <li>Publicly available sources (which we may be required to pay a fee to access), including
        data in the public domain such as Companies' House, your website or professional and
        personal social networking websites like LinkedIn, Facebook, Instagram amongst others and
        any publicly reported court case where you are a named party.
      </li>
      <li>Credit reference agencies and agencies who assist us with 'Know Your Client' checks to
        verify the accuracy of your information.
      </li>
    </ul>
  </section>

  <section class="mb-8">
    <h2 class="text-xl font-bold mb-4 underline">How Do We Use Your Personal Data?</h2>

    <h3 class="text-xl font-bold mb-4 underline">Lawful Basis for Processing Your Personal
      Data</h3>
    <p class="mb-4">We will only use your personal data when the law allows us to do so, i.e.
      where we have a lawful basis for processing it. Most commonly, we will use your personal
      data in the following circumstances:</p>

    <ul class="list-disc pl-8 mb-4 space-y-2">
      <li>Where we need to <strong>perform the contract,</strong> we are about to enter into or
        have entered into with you or take any steps you ask us to before entering into a contract
        with you.
      </li>
      <li>Where it is necessary to do so in order to comply with any <strong>legal
        obligations</strong> we have, such as under money laundering laws.
      </li>
      <li>On the basis of <strong>consent</strong>:
        <ul class="list-disc pl-8 mt-2 space-y-1">
          <li>Where we rely on your consent for processing this will be brought to your attention
            when the information is collected from you.
          </li>
          <li>You have the right to withdraw consent at any time, see the "Your Rights" section
            below for further information about how you may withdraw your consent.
          </li>
          <li>We do not rely on or require your consent for the majority of our processing.</li>
        </ul>
      </li>
      <li>Where the processing is necessary for our legitimate interests in:
        <ul class="list-disc pl-8 mt-2 space-y-1">
          <li>providing legal and/or advisory services;</li>
          <li>ensuring regulatory compliance and maintaining accreditations;</li>
          <li>providing our clients with the best service;</li>
          <li>promoting our services;</li>
          <li>receiving feedback; and</li>
          <li>improving our services and identifying ways to grow our business; and/or</li>
          <li>for the legitimate purposes of our clients or other third parties in receiving those
            services.
          </li>
        </ul>
      </li>
    </ul>

    <p class="mb-4">We will only rely on this lawful basis where we consider that your interests
      and fundamental rights do not override such interests.</p>

    <h4 class="text-lg font-semibold mb-2 italic">Special category data</h4>
    <p class="mb-4">In most cases, when we process special category data (see "<b>What Personal
      Data
      Do We Collect?</b>" for what data this includes) it is because it is necessary to do so for
      the
      establishment, exercise or defence of legal claims or whenever instructed by the courts
      acting in their judicial capacity. We may however process special categories of data with
      your explicit consent (for example for the purpose of providing legal advice), where it is
      necessary to protect your vital interests (for example in the event of an emergency where
      you are attending our premises) or where the data has been manifestly made public by
      you.</p>

    <h3 class="text-xl font-bold mb-4 underline">Purposes for which we use your personal data</h3>
    <p class="mb-4">We may process your personal data for the following purposes:</p>

    <ul class="list-disc pl-8 mb-4 space-y-2">
      <li>Pursuant to our contract to provide you with our services, or to take steps linked to a
        contract with you or your organisation. This includes:
        <ul class="list-disc pl-8 mt-2 space-y-1">
          <li>To register you as a client.</li>
          <li>To provide and administer legal services as requested by you, including updating you
            in relation to the progress of your case and requesting information or documentation
            from you as required to promote your case.
          </li>
          <li>To process payments, billing and collection of any debts owed to us.</li>
          <li>To send you transactional and service emails (the email address you provide may be
            used to provide you with notices, updates, security alerts, information regarding
            changes to our policies, and support and administrative messages).
          </li>
        </ul>
      </li>
      <li>As required by us to conduct our business and pursue our legitimate interests,
        including:
        <ul class="list-disc pl-8 mt-2 space-y-1">
          <li>To provide and administer legal services as requested by you.</li>
          <li>To prevent and address fraud, breach of policies or terms, and threats or harm.</li>
          <li>To analyse and improve our <a
            class="italic font-bold text-green-600 hover:underline"
            href="https://johnsonlawgroup.co.uk/">Website</a> or other JLG Group websites, apps,
            marketing
            efforts, products and services (we continually strive to improve our offerings based
            on the information and feedback we receive from you).
          </li>
          <li>To personalise your experience and improve the quality of our communications with
            you (your information helps us to better respond to your individual needs).
          </li>
          <li>To develop and send you direct marketing communications, including advertisements
            and promotional communications about our services, other group action claims that we
            are or may be accepting new clients for, and events.
          </li>
          <li>To utilise centralised services for JLG Group.</li>
          <li>To administer and manage our relationship with you, including accounting, auditing,
            and taking other steps linked to the performance of our business relationship.
          </li>
          <li>To manage access to our premises and for security purposes.</li>
          <li>To protect the security of our communications and other systems and to prevent and
            detect security threats, frauds or other criminal or malicious activities.
          </li>
          <li>For insurance purposes.</li>
          <li>To exercise or defend our legal rights, including bringing or defending legal claims
            on our own behalf.
          </li>
          <li>To ask you to provide your feedback or fill out a survey.</li>
        </ul>
      </li>
      <li>On the basis of your consent:
        <ul class="list-disc pl-8 mt-2 space-y-1">
          <li>To fulfil any other purpose disclosed to you and with your consent. Where consent is
            relied upon this will be brought to your attention when consent is granted.
          </li>
        </ul>
      </li>
      <li>To comply with legal requirements relating to:
        <ul class="list-disc pl-8 mt-2 space-y-1">
          <li>the provision of regulated products and services;</li>
          <li>data protection;</li>
          <li>tax;</li>
          <li>health and safety;</li>
          <li>anti-money laundering, fraud investigations or other crime;</li>
          <li>anti-discrimination;</li>
          <li>mandatory reporting obligations;</li>
          <li>assisting law enforcement; and</li>
          <li>any other legal obligations placed on us from time to time.</li>
        </ul>
      </li>
    </ul>

    <p class="mb-4">Notwithstanding the above, we may use information in connection with the
      services provided that does not identify you (including information that has been aggregated
      or de-identified) for any purpose except as prohibited by applicable law. For information on
      your rights and choices regarding how we use information about you, please see the <i>"Your
        Rights"</i> section below.</p>

    <h4 class="text-lg font-semibold mb-2 italic">Marketing Communications Choices</h4>
    <p class="mb-4">We strive to provide you with choices regarding certain personal data uses,
      particularly around marketing and advertising.</p>
    <p class="mb-4">We may use your personal data to form a view on what we think you may want or
      need, or what may be of interest to you. This is how we decide which services and offers may
      be relevant for you (we call this marketing).</p>
    <p class="mb-4">You will receive marketing communications via email and text from us if you
      have:</p>
    <ul class="list-disc pl-8 mb-4 space-y-2">
      <li>opted-in to receive these via text, at an event or by any other opt-in mechanism; or
      </li>
      <li>you have requested information from us or paid for our services and you have not opted
        out of receiving those communications.
      </li>
    </ul>

    <h4 class="text-lg font-semibold mb-2 italic">Opting out and withdrawing consent</h4>
    <p class="mb-4">You can ask us to stop sending you marketing communications at any time using
      the following unsubscribe methods:</p>
    <ul class="list-disc pl-8 mb-4 space-y-2">
      <li>Emails. Should you wish to stop receiving further promotional emails from us, please
        follow the instructions as provided in the promotional emails you receive to click on the
        unsubscribe link or email us at <a href="mailto:communications@johnsonlawgroup.co.uk"
                                           class="text-green-600 hover:underline">communications@johnsonlawgroup.co.uk</a>
        with the word UNSUBSCRIBE in the subject field of the email. Please note that you cannot
        opt-out of non-promotional emails, such as those about your ongoing business relations
        with JLG UK -- however you can change your preferred contact method, see "Our
        Communication With You About Your Case" section below for further details.
      </li>
      <li>Text Messages. You can opt-out of receiving text messages to your phone number at any
        time by texting "STOP" in response to any text message you receive from us or contacting
        us as set forth in the section entitled "Contact Us" below and specifying you want to
        opt-out of text messages.
      </li>
    </ul>
    <p class="mb-4">Please note that your opt-out is limited to the email address and phone number
      used and will not affect subsequent subscriptions, or any marketing material we send to you
      via other means.</p>

    <h4 class="text-lg font-semibold mb-2 italic">Our Communication With You About Your Case</h4>
    <p class="mb-4">We typically communicate with our clients through the following means:</p>
    <ul class="list-disc pl-8 mb-4 space-y-2">
      <li>E-mail</li>
      <li>Text messaging (also referred to as SMS)</li>
      <li>WhatsApp</li>
      <li>Facebook Messenger</li>
      <li>Letters by post</li>
      <li>Telephone calls</li>
    </ul>

    <p class="mb-4">We always aim to contact you via your preferred contact method(s). If you
      change your mind about your preferred contact method(s) at any time, please let us know by
      emailing <a href="mailto:communications@johnsonlawgroup.co.uk"
                  class="text-green-600 hover:underline">communications@johnsonlawgroup.co.uk</a>.
      <span class="underline">Please state clearly which form of communication you no longer wish to be contacted via.</span>
    </p>
  </section>

  <section class="mb-8" id="Cookies">
    <h2 class="text-xl font-bold mb-4">Cookies</h2>

    <h3 class="text-xl font-semibold mb-2 underline">What are cookies?</h3>
    <p class="mb-4">A cookie is a small file of letters and numbers that we store on your browser
      or the hard drive of your computer if you agree. Cookies contain information that is
      transferred to your computer's hard drive.</p>
    <p class="mb-4">Our Website uses cookies and other similar technologies (together referred to
      as <b>"cookies"</b>) to distinguish you from other users of our Website. This helps us to
      provide
      you with a good experience when you browse our Website and also allows us to improve our
      site. For example, we use cookies to enable certain features (eg. logging in), to track site
      usage (eg. analytics), to store your user settings (eg. time zone preferences), and to
      personalise your content (eg. language).</p>

    <h3 class="text-xl font-semibold mb-2 underline">What types of cookies do we use?</h3>
    <p class="mb-4">We use the following types of cookies:</p>
    <ul class="list-disc pl-8 mb-4 space-y-2">
      <li><strong>Essential cookies:</strong> these cookies are strictly necessary for the
        operation of the site. For example, cookies that help ensure that the content of a page
        loads quickly and effectively.
      </li>
      <li><strong>Functionality cookies:</strong> these cookies are important for the site to
        operate, although not strictly necessary. For example, a cookie that remembers the
        settings you may configure on the Website (like language and time zone settings).
      </li>
      <li><strong>Analytics/tracking cookies:</strong> these cookies analyse or predict your
        behaviour on the site. For example, by monitoring your movements around the site.
      </li>
      <li><strong>Marketing cookies:</strong> these cookies track and profile users for direct
        marketing and advertising. For example, a cookie that tracks someone from a site across to
        a social media site, and shows adverts on that user's social media page relating to the
        original site.
      </li>
    </ul>

    <p class="mb-4">Cookies can be first party (set by the website that the user is visiting) or
      third party (set by a domain other than the one the user is visiting). They can also be
      session (last for the duration of a session on the website) or persistent (continue once
      that session has ended).</p>
    <p class="mb-4">The tables below explain the cookies we use and why.</p>
    <p class="mb-4">For more information about how to change your cookie preferences, please see
      below.</p>

    <h3 class="text-xl font-semibold mb-2 underline">First Party Cookies</h3>
    <p class="mb-4">Not applicable</p>

    <h3 class="text-xl font-semibold mb-2 underline">Third-party cookies</h3>
    <p class="mb-4">Some of the cookies we use are set by third parties. We work with other
      companies, such as search engine providers, so that we can learn more about our customers,
      in order to understand performance and engagement, provide a personalised experience and
      deliver more relevant advertising. The table below further explains the third party cookies
      that are used on our Website and their purposes.</p>
    <p class="mb-4">To learn more about how these third parties process personal data, to the
      extent relevant, follow the links in the table below to the third party privacy notice.</p>

    <div class="overflow-x-auto">
      <table class="min-w-full bg-white border border-gray-300 mb-4">
        <thead class="bg-gray-100">
        <tr>
          <th class="py-2 px-3 border-b text-left font-semibold">Third Party</th>
          <th class="py-2 px-3 border-b text-left font-semibold">Purpose</th>
          <th class="py-2 px-3 border-b text-left font-semibold">Cookie duration</th>
          <th class="py-2 px-3 border-b text-left font-semibold">Link to third party privacy
            notice
          </th>
        </tr>
        </thead>

        <tbody>
        <tr>
          <td colspan="4" class="py-2 px-3 border-b">Essential cookies</td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">Not Used</td>
          <td class="py-2 px-3 border-b"></td>
          <td class="py-2 px-3 border-b"></td>
          <td class="py-2 px-3 border-b"></td>
        </tr>
        <tr>
          <td colspan="4" class="py-2 px-3 border-b">Functionality cookies</td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">Cookiebot</td>
          <td class="py-2 px-3 border-b">
            Stores the user cookie state for current domain
          </td>
          <td class="py-2 px-3 border-b">1 year</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://www.cookiebot.com/en/privacy-policy/"
            >https://www.cookiebot.com/en/privacy-policy/</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">1.gif</td>
          <td class="py-2 px-3 border-b">
            Used to count the number of sessions to the website, necessary for
            optimizing CMP product delivery
          </td>
          <td class="py-2 px-3 border-b">Session</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://www.cookiebot.com/en/privacy-policy/"
            >https://www.cookiebot.com/en/privacy-policy/</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">test_cookie</td>
          <td class="py-2 px-3 border-b">
            Used to check if user’s browser supports cookies
          </td>
          <td class="py-2 px-3 border-b">1 day</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://policies.google.com/privacy"
            >https://policies.google.com/privacy</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">CONSENT</td>
          <td class="py-2 px-3 border-b">
            Used to detect if the visitor has accepted the marketing category in
            the cookie banner. Necessary for GDPR-compliance
          </td>
          <td class="py-2 px-3 border-b">2 years</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://policies.google.com/privacy"
            >https://policies.google.com/privacy</a
            >
          </td>
        </tr>
        <tr>
          <td colspan="4" class="py-2 px-3 border-b">
            Analytics/tracking cookies
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">_ga[x2]</td>
          <td class="py-2 px-3 border-b">
            Registers a unique ID that is used to generate statistical data on
            how the visitor uses the site
          </td>
          <td class="py-2 px-3 border-b">2 years</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://policies.google.com/privacy"
            >https://policies.google.com/privacy</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">_ga_#[x2]</td>
          <td class="py-2 px-3 border-b">
            Used by google analytics to collect data on the number of times a
            user has visited the website as well as the dates for the first and
            most recent visit
          </td>
          <td class="py-2 px-3 border-b">2 years</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://policies.google.com/privacy"
            >https://policies.google.com/privacy</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">Personalization_id</td>
          <td class="py-2 px-3 border-b">
            Used by twitter to allow the visitor to share content from the
            website onto their twitter profile
          </td>
          <td class="py-2 px-3 border-b">400 days</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://twitter.com/en/privacy"
            >https://twitter.com/en/privacy</a
            >
          </td>
        </tr>
        <tr>
          <td colspan="4" class="py-2 px-3 border-b">Marketing cookies</td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">_fbp&nbsp;[x2]</td>
          <td class="py-2 px-3 border-b">
            Used by Facebook to deliver a series of advertisement products such
            as real time bidding from third party advertisers.
          </td>
          <td class="py-2 px-3 border-b">3 months</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://www.facebook.com/privacy/policy/?entry_point=data_policy_redirect&amp;entry=0"
            >https://www.facebook.com/privacy/policy/?entry_point=data_policy_redirect&amp;entry=0</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">lastExternalReferrer</td>
          <td class="py-2 px-3 border-b">
            Detects how the user reached the website by registering their last
            URL-address.
          </td>
          <td class="py-2 px-3 border-b">Persistent</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://www.facebook.com/privacy/policy/?entry_point=data_policy_redirect&amp;entry=0"
            >https://www.facebook.com/privacy/policy/?entry_point=data_policy_redirect&amp;entry=0</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">lastExternalReferrerTime</td>
          <td class="py-2 px-3 border-b">
            Detects how the user reached the website by registering their last
            URL-address.
          </td>
          <td class="py-2 px-3 border-b">Persistent</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://www.facebook.com/privacy/policy/?entry_point=data_policy_redirect&amp;entry=0"
            >https://www.facebook.com/privacy/policy/?entry_point=data_policy_redirect&amp;entry=0</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">_gcl_au&nbsp;[x2]</td>
          <td class="py-2 px-3 border-b">
            Used by Google AdSense for experimenting with advertisement
            efficiency across websites using their services.
          </td>
          <td class="py-2 px-3 border-b">3 months</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://policies.google.com/privacy"
            >https://policies.google.com/privacy</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">IDE</td>
          <td class="py-2 px-3 border-b">
            Used by Google DoubleClick to register and report the website user's
            actions after viewing or clicking one of the advertiser's ads with
            the purpose of measuring the efficacy of an ad and to present
            targeted ads to the user.
          </td>
          <td class="py-2 px-3 border-b">1 year</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://policies.google.com/privacy"
            >https://policies.google.com/privacy</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">pagead/landing</td>
          <td class="py-2 px-3 border-b">
            Collects data on visitor behaviour from multiple websites, in order
            to present more relevant advertisement - This also allows the
            website to limit the number of times that they are shown the same
            advertisement.
          </td>
          <td class="py-2 px-3 border-b">Session</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://policies.google.com/privacy"
            >https://policies.google.com/privacy</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">ads/ga-audiences</td>
          <td class="py-2 px-3 border-b">
            Used by Google AdWords to re-engage visitors that are likely to
            convert to customers based on the visitor's online behaviour across
            websites.
          </td>
          <td class="py-2 px-3 border-b">Session</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://policies.google.com/privacy"
            >https://policies.google.com/privacy</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">_uetsid</td>
          <td class="py-2 px-3 border-b">
            Used to track visitors on multiple websites, in order to present
            relevant advertisement based on the visitor's preferences.
          </td>
          <td class="py-2 px-3 border-b">Persistent</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://policies.google.com/privacy"
            >https://policies.google.com/privacy</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">_uetsid_exp</td>
          <td class="py-2 px-3 border-b">
            Contains the expiry-date for the cookie with corresponding name.
          </td>
          <td class="py-2 px-3 border-b">Persistent</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://privacy.microsoft.com/en-US/privacystatement"
            >https://privacy.microsoft.com/en-US/privacystatement</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">_uetvid</td>
          <td class="py-2 px-3 border-b">
            Used to track visitors on multiple websites, in order to present
            relevant advertisement based on the visitor's preferences.
          </td>
          <td class="py-2 px-3 border-b">Persistent</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://privacy.microsoft.com/en-US/privacystatement"
            >https://privacy.microsoft.com/en-US/privacystatement</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">_uetvid_exp</td>
          <td class="py-2 px-3 border-b">
            Contains the expiry-date for the cookie with corresponding name.
          </td>
          <td class="py-2 px-3 border-b">Persistent</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://privacy.microsoft.com/en-US/privacystatement"
            >https://privacy.microsoft.com/en-US/privacystatement</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">MSPTC</td>
          <td class="py-2 px-3 border-b">Pending</td>
          <td class="py-2 px-3 border-b">1 year</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://privacy.microsoft.com/en-US/privacystatement"
            >https://privacy.microsoft.com/en-US/privacystatement</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">MUID</td>
          <td class="py-2 px-3 border-b">
            Used widely by Microsoft as a unique user ID. The cookie enables
            user tracking by synchronising the ID across many Microsoft domains.
          </td>
          <td class="py-2 px-3 border-b">1 year</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://privacy.microsoft.com/en-US/privacystatement"
            >https://privacy.microsoft.com/en-US/privacystatement</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">_uetsid&nbsp;[x2]</td>
          <td class="py-2 px-3 border-b">
            Collects data on visitor behaviour from multiple websites, in order
            to present more relevant advertisement - This also allows the
            website to limit the number of times that they are shown the same
            advertisement.
          </td>
          <td class="py-2 px-3 border-b">1 day</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://privacy.microsoft.com/en-US/privacystatement"
            >https://privacy.microsoft.com/en-US/privacystatement</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">_uetvid&nbsp;[x2]</td>
          <td class="py-2 px-3 border-b">
            Used to track visitors on multiple websites, in order to present
            relevant advertisement based on the visitor's preferences.
          </td>
          <td class="py-2 px-3 border-b">Session</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://privacy.microsoft.com/en-US/privacystatement"
            >https://privacy.microsoft.com/en-US/privacystatement</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">tt_appInfo</td>
          <td class="py-2 px-3 border-b">
            Used by the social networking service, TikTok, for tracking the use
            of embedded services.
          </td>
          <td class="py-2 px-3 border-b">Session</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://www.tiktok.com/legal/page/us/privacy-policy/en"
            >https://www.tiktok.com/legal/page/us/privacy-policy/en</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">tt_pixel_session_index</td>
          <td class="py-2 px-3 border-b">
            Used by the social networking service, TikTok, for tracking the use
            of embedded services.
          </td>
          <td class="py-2 px-3 border-b">Session</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://www.tiktok.com/legal/page/us/privacy-policy/en"
            >https://www.tiktok.com/legal/page/us/privacy-policy/en</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">tt_sessionId</td>
          <td class="py-2 px-3 border-b">
            Used by the social networking service, TikTok, for tracking the use
            of embedded services.
          </td>
          <td class="py-2 px-3 border-b">Session</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://www.tiktok.com/legal/page/us/privacy-policy/en"
            >https://www.tiktok.com/legal/page/us/privacy-policy/en</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">_ttp</td>
          <td class="py-2 px-3 border-b">
            Used by the social networking service, TikTok, for tracking the use
            of embedded services.
          </td>
          <td class="py-2 px-3 border-b">1 year</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://www.tiktok.com/legal/page/us/privacy-policy/en"
            >https://www.tiktok.com/legal/page/us/privacy-policy/en</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">i/adsct&nbsp;[x2]</td>
          <td class="py-2 px-3 border-b">
            The cookie is used by Twitter.com in order to determine the number
            of visitors accessing the website through Twitter advertisement
            content.
          </td>
          <td class="py-2 px-3 border-b">Session</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://twitter.com/en/privacy"
            >https://twitter.com/en/privacy</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">muc_ads</td>
          <td class="py-2 px-3 border-b">
            Collects data on user behaviour and interaction in order to optimize
            the website and make advertisement on the website more relevant.
          </td>
          <td class="py-2 px-3 border-b">400 days</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://twitter.com/en/privacy"
            >https://twitter.com/en/privacy</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">LAST_RESULT_ENTRY_KEY</td>
          <td class="py-2 px-3 border-b">
            Used to track user’s interaction with embedded content.
          </td>
          <td class="py-2 px-3 border-b">Session</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://policies.google.com/privacy"
            >https://policies.google.com/privacy</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">
            LogsDatabaseV2:V#||LogsRequestsStore
          </td>
          <td class="py-2 px-3 border-b">Pending</td>
          <td class="py-2 px-3 border-b">Persistent</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://policies.google.com/privacy"
            >https://policies.google.com/privacy</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">remote_sid</td>
          <td class="py-2 px-3 border-b">
            Necessary for the implementation and functionality of YouTube
            video-content on the website.
          </td>
          <td class="py-2 px-3 border-b">1 day</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://policies.google.com/privacy"
            >https://policies.google.com/privacy</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">TESTCOOKIESENABLED</td>
          <td class="py-2 px-3 border-b">
            Used to track user’s interaction with embedded content.
          </td>
          <td class="py-2 px-3 border-b">1 day</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://policies.google.com/privacy"
            >https://policies.google.com/privacy</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">VISITOR_INFO1_LIVE</td>
          <td class="py-2 px-3 border-b">
            Tries to estimate the users' bandwidth on pages with integrated
            YouTube videos.
          </td>
          <td class="py-2 px-3 border-b">180 days</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://policies.google.com/privacy"
            >https://policies.google.com/privacy</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">VISITOR_PRIVACY_METADATA</td>
          <td class="py-2 px-3 border-b">
            Stores the user's cookie consent state for the current domain
          </td>
          <td class="py-2 px-3 border-b">180 days</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://policies.google.com/privacy"
            >https://policies.google.com/privacy</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">YSC</td>
          <td class="py-2 px-3 border-b">
            Registers a unique ID to keep statistics of what videos from YouTube
            the user has seen.
          </td>
          <td class="py-2 px-3 border-b">Session</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://policies.google.com/privacy"
            >https://policies.google.com/privacy</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">YtIdbMeta#databases</td>
          <td class="py-2 px-3 border-b">
            Used to track user’s interaction with embedded content.
          </td>
          <td class="py-2 px-3 border-b">Persistent</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://policies.google.com/privacy"
            >https://policies.google.com/privacy</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">yt-remote-cast-available</td>
          <td class="py-2 px-3 border-b">
            Stores the user's video player preferences using embedded YouTube
            video
          </td>
          <td class="py-2 px-3 border-b">Session</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://policies.google.com/privacy"
            >https://policies.google.com/privacy</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">yt-remote-cast-installed</td>
          <td class="py-2 px-3 border-b">
            Stores the user's video player preferences using embedded YouTube
            video
          </td>
          <td class="py-2 px-3 border-b">Session</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://policies.google.com/privacy"
            >https://policies.google.com/privacy</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">yt-remote-device-id</td>
          <td class="py-2 px-3 border-b">
            Stores the user's video player preferences using embedded YouTube
            video
          </td>
          <td class="py-2 px-3 border-b">Persistent</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://policies.google.com/privacy"
            >https://policies.google.com/privacy</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">yt-remote-fast-check-period</td>
          <td class="py-2 px-3 border-b">
            Stores the user's video player preferences using embedded YouTube
            video
          </td>
          <td class="py-2 px-3 border-b">Persistent</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://policies.google.com/privacy"
            >https://policies.google.com/privacy</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">yt-remote-fast-check-period</td>
          <td class="py-2 px-3 border-b">
            Stores the user's video player preferences using embedded YouTube
            video
          </td>
          <td class="py-2 px-3 border-b">Session</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://policies.google.com/privacy"
            >https://policies.google.com/privacy</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">yt-remote-session-app</td>
          <td class="py-2 px-3 border-b">
            Stores the user's video player preferences using embedded YouTube
            video
          </td>
          <td class="py-2 px-3 border-b">Session</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://policies.google.com/privacy"
            >https://policies.google.com/privacy</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">yt-remote-session-name</td>
          <td class="py-2 px-3 border-b">
            Stores the user's video player preferences using embedded YouTube
            video
          </td>
          <td class="py-2 px-3 border-b">Session</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://policies.google.com/privacy"
            >https://policies.google.com/privacy</a
            >
          </td>
        </tr>
        <tr>
          <td class="py-2 px-3 border-b">dicbo_id</td>
          <td class="py-2 px-3 border-b">pending</td>
          <td class="py-2 px-3 border-b">1 day</td>
          <td class="py-2 px-3 border-b">
            <a
              class="text-green-600 hover:underline"
              href="https://www.outbrain.com/privacy/#privacy-policy"
            >https://www.outbrain.com/privacy/#privacy-policy</a
            >
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <h3 class="text-xl mb-2 underline italic">How you can change your cookie
      preferences</h3>
    <p class="mb-4">You can control which types of cookies are set by changing your preferences
      through our cookie banner when you first visit our Website or at any time by clicking the
      link labeled "Renew or change your cookie consent" located at the bottom of every web page
      on our website.</p>
    <p class="mb-4">Cookies can also be controlled by your web browser settings. Whether our
      cookies are used will depend on your browser settings, so you are in control. You can block
      cookies by activating the setting on your browser that allows you to refuse the setting of
      all or some cookies. However, if you use your browser settings to block all cookies
      (including essential cookies) you may not be able to access all or parts of our Website and
      you will not receive a personalised service.</p>
    <p class="mb-4">To find out more about cookies, including how to see what cookies have been
      set and how to manage and delete them, please use the following links:</p>
    <ul class="list-disc pl-8 mb-4 space-y-2">
      <li><a href="http://support.microsoft.com/kb/278835" class="text-green-600 hover:underline">Internet
        Explorer cookies information</a></li>

      <li><a href="https://support.google.com/chrome/answer/95647?hl=en-GB"
             class="text-green-600 hover:underline">Chrome
        cookies information</a></li>
      <li><a href="http://support.mozilla.org/en-US/kb/Clear%20Recent%20History"
             class="text-green-600 hover:underline">Firefox
        cookies information</a></li>
      <li><a href="http://support.apple.com/kb/PH5042" class="text-green-600 hover:underline">Safari
        cookies information</a></li>
    </ul>
  </section>

  <section class="mb-8">
    <h2 class="text-xl italic mb-6 underline">Who might we share your personal
      data with?</h2>

    <p class="mb-4">For the purposes set out in the <i>"How Do We Use Your Personal Data?"</i>
      section
      above, we may share your personal data with the following categories of third parties, some
      of whom we appoint to provide services, including as follows:</p>

    <ul class="list-disc pl-8 mb-6 space-y-2">
      <li>Our clients. As part of the provision of legal services to them.</li>

      <li>Where you have been appointed as a member of a claimant committee on any given case, we
        may share your personal contact details with other committee members to enable you to
        confer and collaborate with other members of the committee as required.
      </li>

      <li>Professional advisors, suppliers and sub-contractors in the course of the provision of
        legal or other services or in the performance of any contract we enter into with you. This
        will depend on the legal work we are instructed to carry out but includes parties such as
        other law firms, barristers, expert witnesses, investigators, courts, registries and
        regulators.
      </li>

      <li>Third parties who have a right to access information through legal disclosure rules as
        part of litigation or to whom we believe release of your data is appropriate to comply
        with the law or legal process, and where required, in response to lawful requests by
        public authorities, including to meet national security or law enforcement requirements.
      </li>

      <li>Service providers. Third parties who provide us with services such as website operation
        and hosting, system administration, technology, data analytics, marketing and advertising,
        business development, accountancy and bookkeeping, banking, insurance, litigation support,
        security and technical and operational support. Service Providers are prohibited from
        using your information for any reason other than to provide these services, although we
        may permit them to use information that does not identify you (including information that
        has been aggregated or de-identified) for any purpose except as prohibited by applicable
        law.
      </li>

      <li>Event venues, webinar hosts and training providers. To provide you with access to our
        events and training.
      </li>

      <li>Our lenders and/or funders. We may be required to share your personal data with our
        lenders and/or funders in order for them to perform valuation calculations in respect of
        loans; or, if we default on a loan, to allow it to be enforced.
      </li>

      <li>Our Regulators. We may be required to disclose personal data about our clients to our
        regulators to comply with our regulatory requirements and to submit to regulatory audits.
        Our regulator in the UK is the Solicitors' Regulatory Authority (SRA).
      </li>
    </ul>

    <p class="mb-4">Additionally, we will disclose your personal data to the relevant third
      party:</p>

    <ul class="list-disc pl-8 mb-6 space-y-2">
      <li>In the event of a merger or acquisition. We will share information in connection with,
        or during negotiations of, any proposed or actual merger, purchase, sale or any other type
        of acquisition or business combination of all or any portion of our assets, or transfer of
        all or a portion of our business to another business.
      </li>

      <li>If we are under a duty to share your personal data in order to comply with any legal
        obligation, or in order to enforce or apply our terms of use and other agreements; or to
        protect the rights, property or safety of our clients, our regulator or others.
      </li>

      <li>Where we have your consent to do so. Where we process your information on the basis of
        your consent, we will bring this to your attention when the consent is obtained.
      </li>
    </ul>

    <p class="mb-6">Notwithstanding the above, we may share information that does not identify you
      i.e. anonymous data (including information that has been aggregated or de-identified) with
      third parties except as prohibited by applicable law.</p>

    <h2 class="text-xl italic mb-4 underline">International Transfers</h2>

    <p class="mb-6">We do not transfer your personal data outside the UK. If we are required to
      transfer your personal data outside the UK, we will put appropriate safeguards in place,
      such as an International Data Transfer Agreement or Standard Contractual Clauses, where
      these are required by applicable data protection law.</p>

    <h2 class="text-xl italic mb-4 underline">How Long Do We Keep Your Personal
      Data?</h2>

    <p class="mb-6">We only keep your information for as long as we need it for the purposes for
      which we process it (as set out above in <a class="text-green-600 hover:underline"
                                                  href="https://claims.johnsonlawgroup.co.uk/privacy-policy.php#Purposesforwhichweuseyourpersonaldata">"Purposes
        for which we use your personal data"</a>).
      This includes for the purposes of satisfying any legal, regulatory, tax, accounting or
      reporting requirements.</p>

    <p class="mb-6">We keep the majority of your personal data for a maximum of 6 years after the
      end of our relationship with you unless we are otherwise required to delete it due to a
      legal obligation or a rights request. Our relationship with you typically ends once our
      services are no longer required, your claim has concluded, and our fees have been paid. We
      may retain your personal data for a longer period in the event of a complaint or if we
      reasonably believe there is a prospect of litigation in respect to our relationship with
      you.</p>

    <h2 class="text-xl italic mb-4 underline">How Do We Protect Your Personal
      Data?</h2>

    <p class="mb-6">We implement a variety of reasonable administrative, physical, and technical
      security measures to help protect your information from loss, theft, misuse and unauthorised
      access, disclosure, alteration and destruction. In addition, we limit access to your
      personal data to those employees, agents, contractors and other third parties who have a
      business need to know. They will only process your personal data on our instructions and
      they are subject to a duty of confidentiality.</p>

    <p class="mb-6">We have put in place procedures to deal with any suspected personal data
      breach and will notify you and any applicable regulator of a breach where we are legally
      required to do so.</p>

    <p class="mb-6">Transmission via the internet is not completely secure and we cannot guarantee
      the security of your personal data.</p>

    <h2 class="text-xl italic mb-4 underline">Your Rights</h2>

    <p class="mb-4">Under certain circumstances, you have rights under data protection laws in
      relation to your personal data. You have the right to:</p>

    <ul class="list-disc pl-8 mb-6 space-y-2">
      <li><strong class="font-bold">Withdraw consent at any time</strong> where we are relying on
        consent to process your personal data. However, this will not affect the lawfulness of any
        processing carried out before you withdraw your consent. If you withdraw your consent, we
        may not be able to provide certain services to you. We will advise you if this is the case
        at the time you withdraw your consent.
      </li>

      <li><strong class="font-bold">Object to our processing</strong> of your personal data for
        <strong class="font-bold">direct marketing purposes</strong>. We will inform you (before
        collecting your data) if we intend to use your data for such purposes or if we intend to
        disclose your information to any third party for such purposes.
      </li>

      <li><strong class="font-bold">Object to our processing</strong> of your personal data where
        it is processed on the basis of our <strong class="font-bold">legitimate
          interests</strong> (or those of a third party) and there is something about your
        particular situation which makes you want to object to processing on this ground as you
        feel it impacts on your fundamental rights and freedoms. In some cases, we may demonstrate
        that there are compelling legitimate reasons for that processing which override your
        rights and freedoms.
        <ul class="list-disc pl-8 mt-2">
          <li><strong class="font-bold">Object to our processing</strong> of your personal data
            for <strong class="font-bold">scientific or historical research purposes, or
              statistical purposes,</strong> where relevant, unless the processing is necessary in
            the public interest.
          </li>
        </ul>
      </li>

      <li><strong class="font-bold">Request access</strong> to your personal data (commonly known
        as a "data subject access request"). This enables you to request a copy of the personal
        data we hold about you and to check that we are lawfully processing it.
      </li>

      <li><strong class="font-bold">Request rectification</strong> of the personal data we hold
        about you if it is inaccurate or incomplete. This enables you to have that data corrected,
        though we may need to verify the accuracy of the new data you provide to us.
      </li>

      <li><strong class="font-bold">Request erasure</strong> of the personal data we hold about
        you, provided that the personal data is:
        <ul class="list-disc pl-8 mt-2">
          <li>no longer necessary for the purposes for which it was collected;</li>
          <li>you withdraw consent (if the legal basis for processing is consent);</li>
          <li>you exercise your right to object, set out above, and there are no overriding
            legitimate grounds for processing;
          </li>
          <li>the data is unlawfully processed by us;</li>
          <li>the data needs to be erased to comply with a legal obligation; or</li>
          <li>the data is children's data and was collected in relation to an offer of information
            society services;
          </li>
        </ul>
      </li>

      <li><strong class="font-bold">Request restriction of processing</strong> of the personal
        data we hold about you where:
        <ul class="list-disc pl-8 mt-2">
          <li>you want us to establish the data's accuracy;</li>
          <li>we have unlawfully processed it, but you do not want us to erase it;</li>
          <li>the personal data is no longer necessary for the purposes for which it was
            collected, but you want us to keep it in order to create, exercise or defend legal
            claims; or
          </li>
          <li>you exercise your right to object (pending verification of whether there are
            legitimate grounds for processing);
          </li>
        </ul>
      </li>

      <li><strong class="font-bold">Request the transfer</strong> of the personal data we hold
        about you to you or a third party, only if the processing is carried out by automated
        means and the legal basis for the processing is consent or for the performance of a
        contract with you. We will provide to you, or a third party you have chosen, your personal
        data in a structured, commonly used, machine-readable format.
      </li>
    </ul>

    <p class="mb-6">You may exercise your rights by submitting a request to us using the contact
      details set out in the <i>"Contact Us"</i>
      section below.</p>

    <p class="mb-6">Should you have any issues, concerns or problems in relation to your data, or
      wish to notify us of data which is inaccurate, please let us know using the contact details
      set out in the <a
        href="https://claims.johnsonlawgroup.co.uk/privacy-policy.php#ContactUs"
        class="text-green-600 hover:underline">"Contact Us"</a> section below.</p>

    <h3 class="text-xl mb-2 italic">Time limit to respond</h3>
    <p class="mb-4">We will aim to respond to your request within 1 month, unless the request is
      complex or you have made a number of requests, in which case, we may require up to an
      additional 2 months to provide a full response. We will notify you if we need to use such an
      extension, setting out our reasons for the same.</p>

    <h3 class="text-xl mb-2 italic">What we may need from you</h3>
    <p class="mb-4">We may request specific information from you to help us confirm your identity
      and process your request. This is a security measure to ensure that personal data is not
      disclosed to any person who does not have a right to receive it. We may also contact you to
      ask you for further information in relation to your request to speed up our response.</p>

    <h3 class="text-xl mb-2 italic">No fee usually required</h3>
    <p class="mb-6">You will not have to pay a fee to access your personal data (or to exercise
      any of the other rights). However, we may charge a reasonable fee if your request is clearly
      unfounded, repetitive or excessive. Alternatively, we could refuse to comply with your
      request in these circumstances.</p>

    <p class="mb-6">If you are not satisfied with our processing of your personal data, you have
      the right to lodge a complaint at any time with the Information Commissioner's Office (ICO),
      the UK regulator for data protection issues (www.ico.org.uk). However, we would encourage
      you to contact us using the details in the "Contact Us" section below, in the first instance
      using the information below, so that we may try to resolve your complaint before you
      approach the ICO.</p>

    <h2 class="text-xl italic mb-4 underline">Children</h2>

    <p class="mb-6">Our Website and services are directed to people who are at least 16 years old,
      and we do not knowingly collect personal data from children under 16 years of age. If you
      are a parent or guardian and believe JLG UK has collected your child's personal data
      unlawfully, please contact us using the details in the <a
        href="https://claims.johnsonlawgroup.co.uk/privacy-policy.php#ContactUs"
        class="text-green-600 hover:underline">"Contact Us"</a> section below. Please
      note that in the UK, children aged 13 or over are able to provide their own consent.</p>

    <h2 class="text-xl italic mb-4 underline">Changes to our Privacy Policy</h2>

    <p class="mb-6">We reserve the right to revise, alter and reissue this Privacy Policy at any
      time. If we do this, we will post the update Privacy Policy on this page. If the changes are
      material, we may provide you additional notification of those changes via email. This
      version was last updated on 11 JANUARY 2024.</p>

    <h2 class="text-xl italic mb-4 underline">Contact Us</h2>

    <p class="mb-4">If you have any questions regarding this Privacy Policy or our data practices,
      or wish to exercise your rights, you may contact us using the information below.</p>

    <p class="mb-6">Full name of legal entity: JLG Legal Limited (Company number: 12177815) (ICO
      registration number: ZA700324)</p>

    <p class="mb-2">By email: <a href="mailto:privacyofficer@johnsonlawgroup.co.uk"
                                 class="text-green-600 hover:underline">privacyofficer@johnsonlawgroup.co.uk</a>
    </p>

    <p class="mb-2">By web form: <a href="http://www.johnsonlawgroup.co.uk/contact/"
                                    class="text-green-600 hover:underline">www.johnsonlawgroup.co.uk/contact/</a>
    </p>

    <p class="mb-6">By post: JLG Legal Limited, Chancery Place, 50 Brown Street Manchester,
      England, M2 2JT</p>

  </section>

  <footer class="border-t border-gray-300 pt-6 mt-8 text-sm text-gray-600">
    <p class="font-bold mb-2">COPYRIGHT © 2025 JLG LEGAL LIMITED TRADING AS JOHNSON LAW GROUP</p>
    <p class="mb-2">AUTHORISED AND REGULATED BY THE SOLICITORS REGULATION AUTHORITY - FIRM NUMBER
      664813</p>
    <p class="mb-2">ALL RIGHTS RESERVED |
      <a href="https://claims.johnsonlawgroup.co.uk/privacy-policy.php" class="text-green-600">PRIVACY
        POLICY</a> |
      <a href="https://claims.johnsonlawgroup.co.uk/complaints-policy.php" class="text-green-600">COMPLAINTS
        POLICY</a> |
      <a href="https://claims.johnsonlawgroup.co.uk/terms-of-business.php" class="text-green-600">OUR
        TERMS OF BUSINESS</a>
    </p>

    <button class="text-green-600 underline mb-6">Renew or change your cookie consent</button>

    <p class="text-xs">JLG Legal Limited does not accept service of documents by fax or other
      electronic means (including email) unless it has specifically confirmed as such in writing
      in respect of a given court action or dispute in which case any given consent is limited to
      service for the purposes of that case only.</p>
  </footer>
</template>
